/**
 * Squads.
 *
 * A commander does not command thirty-two people. They command a handful of
 * squads, and each squad sorts itself out. That is the only structure that
 * survives contact — a commander issuing individual orders is a commander
 * who has stopped commanding and started micromanaging.
 *
 * So the squad, not the player, is the unit an order is addressed to. Unlike
 * an earlier version of this tool, squads are not a fixed roster of six
 * phonetic names handed down in advance — a real team's squads are whatever
 * its own players call them, and do not exist until somebody is in one. A
 * squad is created the moment its first member joins it (see
 * `assignPlayerSquad` / `createSquad` in `session/sessionDoc.ts`), carrying
 * whatever name that player gave it.
 */

export const UNASSIGNED_SQUAD = "";

/**
 * Colours a squad can be assigned, distinct from the three faction colours so
 * a squad marker is never mistaken for a faction marker.
 *
 * Picked deterministically from the squad's id (see {@link squadColor})
 * rather than stored and synced, so every peer agrees on a squad's colour
 * the instant they see its id — no coordination, and no risk of two peers
 * momentarily disagreeing while a colour choice propagates.
 */
const SQUAD_COLOR_PALETTE = [
  "oklch(0.78 0.14 85)",
  "oklch(0.72 0.14 310)",
  "oklch(0.8 0.13 195)",
  "oklch(0.75 0.14 130)",
  "oklch(0.76 0.13 45)",
  "oklch(0.74 0.12 265)",
  "oklch(0.78 0.13 20)",
  "oklch(0.74 0.13 165)",
  "oklch(0.76 0.14 350)",
  "oklch(0.72 0.12 225)",
] as const;

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/** A squad's colour, picked deterministically from its id. */
export function squadColor(id: string): string {
  if (!id) return "oklch(0.58 0.012 250)";
  return SQUAD_COLOR_PALETTE[hashString(id) % SQUAD_COLOR_PALETTE.length]!;
}

/**
 * Single character shown on the map and in tight UI, from the squad's own
 * name — the first letter of whatever its members called it.
 *
 * On its own this can collide: "Recon" and "Ridge" both start with R, and
 * unlike the fixed Alpha–Foxtrot roster this tool used to have, nothing stops
 * a team naming two squads almost the same thing. Use {@link assignSquadTags}
 * wherever more than one squad is on screen at once — this is only the
 * building block for a single name in isolation.
 */
export function squadTag(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed[0]!.toUpperCase() : "?";
}

/**
 * Unique tags for a set of squads, in one pass.
 *
 * A commander tells squads apart on the map and in the task strip by tag and
 * colour alone — there is no room for a full name next to a 24px pin. Most
 * of the time the first letter is already unique and this is exactly
 * {@link squadTag}. When two squads' names start the same way, everyone after
 * the first gets more of their name instead, and only falls back to a digit
 * once even that runs out — because "R" and "R2" both explain themselves at a
 * glance, but "R" and "R" do not.
 *
 * Order matters: whichever squad is earlier in `squads` keeps the plain
 * single-letter tag on a collision, so callers should pass squads in a stable
 * order (oldest first) rather than however the sync layer happens to return
 * them, or the same squad could read a different tag on different screens.
 */
export function assignSquadTags(
  squads: readonly { readonly id: string; readonly name: string }[],
): ReadonlyMap<string, string> {
  const tags = new Map<string, string>();
  const used = new Set<string>();

  for (const squad of squads) {
    const source = (squad.name || squad.id).trim().toUpperCase();
    let tag = source ? source[0]! : "?";
    let length = 1;

    while (used.has(tag)) {
      length += 1;
      tag = length <= source.length ? source.slice(0, length) : `${source[0] ?? "?"}${length}`;
      // A pathological name (all one repeated character) could still
      // collide past the string's own length — the digit suffix above grows
      // without bound, so this always terminates.
    }

    used.add(tag);
    tags.set(squad.id, tag);
  }

  return tags;
}

/**
 * Orders a commander can give.
 *
 * A verb and a place, not a sentence. Typing prose while your team is under
 * fire does not happen, so an order is two taps: pick what, pick where. The
 * readable text is generated from that.
 */
export type OrderKind =
  | "attack"
  | "defend"
  | "hold"
  | "move"
  | "recon"
  | "resupply"
  | "transport"
  | "extract"
  | "build";

export interface OrderVerb {
  readonly id: OrderKind;
  readonly label: string;
  /** Imperative used when rendering the order text. */
  readonly imperative: string;
  readonly blurb: string;
}

export const ORDER_VERBS: readonly OrderVerb[] = [
  { id: "attack", label: "Attack", imperative: "ATTACK", blurb: "Take the position." },
  { id: "defend", label: "Defend", imperative: "DEFEND", blurb: "Stop them taking it." },
  { id: "hold", label: "Hold", imperative: "HOLD", blurb: "Stay put, do not push." },
  { id: "move", label: "Move", imperative: "MOVE TO", blurb: "Reposition." },
  { id: "recon", label: "Recon", imperative: "RECON", blurb: "Find out what is there." },
  { id: "resupply", label: "Resupply", imperative: "RESUPPLY", blurb: "Get supplies in." },
  {
    id: "transport",
    label: "Transport",
    imperative: "TRANSPORT TO",
    blurb: "Move people there.",
  },
  { id: "extract", label: "Extract", imperative: "EXTRACT FROM", blurb: "Get them out." },
  { id: "build", label: "Build", imperative: "BUILD AT", blurb: "Put a FOB or works up." },
];

const VERBS_BY_ID = new Map<OrderKind, OrderVerb>(ORDER_VERBS.map((verb) => [verb.id, verb]));

export function getOrderVerb(id: OrderKind): OrderVerb | undefined {
  return VERBS_BY_ID.get(id);
}

/** Render an order the way it would be said aloud: "ATTACK D5-7". */
export function orderText(kind: OrderKind, place: string | undefined, note: string): string {
  const verb = VERBS_BY_ID.get(kind);
  const head = verb ? verb.imperative : kind.toUpperCase();
  const body = place ? `${head} ${place}` : head;
  return note.trim().length > 0 ? `${body} — ${note.trim()}` : body;
}
